import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Logo } from '../ui';
import { useTranslation } from 'react-i18next';
import { Fuel, ClipboardList } from 'lucide-react';
import { useFuelControlEnabled } from '../../hooks/useFuelControlEnabled';
import { useOrdenesCargaEnabled } from '../../hooks/useOrdenesCargaEnabled';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavigationItem {
  nameKey: string;
  path: string;
  icon: React.ReactNode;
  requiredPermissions?: string[];
  requireAll?: boolean;
  requireSuperuser?: boolean;
  requiresFuelControl?: boolean;
  requiresOrdenesCarga?: boolean;
}

const navigationItems: NavigationItem[] = [
  {
    nameKey: 'nav.dashboard',
    path: '/dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
      </svg>
    ),
    requiredPermissions: ['dashboard.ver'],
  },
  {
    nameKey: 'nav.reports',
    path: '/reportes',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    requiredPermissions: ['reportes.ver'],
  },
  {
    nameKey: 'nav.analysis',
    path: '/analisis',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    requiredPermissions: ['analisis.ejecutar'],
  },
  {
    nameKey: 'nav.advance_report',
    path: '/informe-avance',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    requiredPermissions: ['analisis.ejecutar'],
  },
  {
    nameKey: 'nav.management',
    path: '/gestion',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    requiredPermissions: ['gestion.ver'],
  },
  {
    nameKey: 'nav.fuel_control',
    path: '/control-combustible',
    icon: <Fuel className="w-5 h-5" />,
    requiredPermissions: ['reportes.ver'],
    requiresFuelControl: true,
  },
  {
    nameKey: 'nav.ordenes_carga',
    path: '/ordenes-carga-combustible',
    icon: <ClipboardList className="w-5 h-5" />,
    requiredPermissions: ['reportes.ver'],
    requiresOrdenesCarga: true,
  },

  {
    nameKey: 'nav.administration',
    path: '/admin',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    requiredPermissions: ['operadores.ver', 'faenas.ver', 'predios.ver', 'rodales.ver', 'insumos.ver', 'maquinas.ver', 'items_chequeo.ver', 'maquinas.administrar', 'operadores.administrar'],
    requireAll: false,
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { usuario, hasPermission, isSuperuser } = useAuth();
  const { habilitado: combustibleHabilitado } = useFuelControlEnabled();
  const { habilitado: ordenesCargaHabilitado } = useOrdenesCargaEnabled();
  const { t, i18n } = useTranslation();
  const toggleLang = () => i18n.changeLanguage(i18n.language === 'es' ? 'pt-BR' : 'es');

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const canAccess = (item: NavigationItem) => {
    // Verificar si requiere ser superuser
    if (item.requireSuperuser) {
      if (!isSuperuser) {
        return false;
      }
    }

    // Módulos opcionales por organización (no todos los clientes los usan)
    if (item.requiresFuelControl && !combustibleHabilitado) {
      return false;
    }

    if (item.requiresOrdenesCarga && !ordenesCargaHabilitado) {
      return false;
    }

    if (!item.requiredPermissions) return true;
    
    if (item.requireAll) {
      return item.requiredPermissions.every(permission => hasPermission(permission));
    } else {
      return item.requiredPermissions.some(permission => hasPermission(permission));
    }
  };

  return (
  <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:block
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <Logo size="sm" />
            <button
              onClick={onClose}
              className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navigationItems.map((item) => {
              if (!canAccess(item)) return null;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={`
                    flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                    ${isActive(item.path)
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  {item.icon}
                  <span>{t(item.nameKey)}</span>
                </Link>
              );
            })}
          </nav>

          {/* User info + language selector */}
          <div className="p-4 border-t border-gray-200 space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-blue-700">
                  {usuario?.nombre_completo?.charAt(0) || usuario?.username?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {usuario?.nombre_completo || usuario?.username}
                </p>
              </div>
            </div>
            {/* Language toggle */}
            <button
              onClick={toggleLang}
              className="w-full flex items-center justify-center px-3 py-1.5 text-base rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              title="Cambiar idioma / Mudar idioma"
            >
              {i18n.language === 'es' ? '🇧🇷' : '🇨🇱'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;